import React, { useState } from 'react';
import { Bell, Check, CheckCheck, Trash2, Loader2 } from 'lucide-react';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from '../../services/notificationService';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';

const VendorNotifications = () => {
  const { showToast } = useToastContext();
  const [filter, setFilter] = useState('all'); // all, unread, read

  const { data: notificationsData, isLoading } = useNotifications({ per_page: 50 });
  
  // Handle the API response structure - data is inside data.data for paginated responses
  const notifications = notificationsData?.data?.data || notificationsData?.data || [];

  const markAsReadMutation = useMarkNotificationAsRead({
    onSuccess: () => {
      showToast('Notification marked as read', 'success');
    },
  });

  const markAllAsReadMutation = useMarkAllNotificationsAsRead({
    onSuccess: () => {
      showToast('All notifications marked as read', 'success');
    },
  });

  const deleteMutation = useDeleteNotification({
    onSuccess: () => {
      showToast('Notification deleted', 'success');
    },
  });

  const filteredNotifications = Array.isArray(notifications) ? notifications.filter(notif => {
    if (filter === 'unread') return !notif.is_read;
    if (filter === 'read') return notif.is_read;
    return true;
  }) : [];

  const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.is_read).length : 0;

  const handleMarkAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    if (unreadCount > 0) {
      markAllAsReadMutation.mutate();
    }
  };

  const handleDelete = (notificationId) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      deleteMutation.mutate(notificationId);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking_request':
        return { icon: '📋', color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'booking_confirmed':
        return { icon: '✅', color: 'text-green-600', bg: 'bg-green-100' };
      case 'booking_cancelled':
        return { icon: '❌', color: 'text-red-600', bg: 'bg-red-100' };
      case 'booking_in_progress':
        return { icon: '▶️', color: 'text-indigo-600', bg: 'bg-indigo-100' };
      case 'booking_completed':
        return { icon: '🏁', color: 'text-green-600', bg: 'bg-green-100' };
      case 'payment_received':
        return { icon: '💰', color: 'text-green-600', bg: 'bg-green-100' };
      case 'payment_pending':
        return { icon: '⏳', color: 'text-yellow-600', bg: 'bg-yellow-100' };
      case 'event_started':
        return { icon: '🎬', color: 'text-indigo-600', bg: 'bg-indigo-100' };
      case 'event_ended':
        return { icon: '🏁', color: 'text-purple-600', bg: 'bg-purple-100' };
      case 'review_received':
        return { icon: '⭐', color: 'text-yellow-600', bg: 'bg-yellow-100' };
      case 'message_received':
        return { icon: '💬', color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'broadcast':
        return { icon: '📢', color: 'text-primary-600', bg: 'bg-primary-100' };
      case 'reminder':
        return { icon: '⏰', color: 'text-orange-600', bg: 'bg-orange-100' };
      case 'system':
        return { icon: '⚙️', color: 'text-gray-600', bg: 'bg-gray-100' };
      default:
        return { icon: '🔔', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) > 1 ? 's' : ''} ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Notifications</h1>
              <p className="text-gray-600 mt-1">
                Stay updated with your bookings and activities
                {unreadCount > 0 && (
                  <span className="ml-2 text-primary-600 font-medium">
                    ({unreadCount} unread)
                  </span>
                )}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllAsRead}
                icon={CheckCheck}
                variant="outline"
                disabled={markAllAsReadMutation.isPending}
                loading={markAllAsReadMutation.isPending}
              >
                Mark All as Read
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'unread'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Unread ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'read'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Read ({notifications.length - unreadCount})
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {filteredNotifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Notifications</h3>
              <p className="text-gray-600">
                {filter === 'unread'
                  ? "You're all caught up! No unread notifications."
                  : filter === 'read'
                  ? 'No read notifications.'
                  : "You don't have any notifications yet."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredNotifications.map((notification) => {
                const { icon, color, bg } = getNotificationIcon(notification.type);
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notification.is_read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 ${bg} rounded-full flex items-center justify-center text-lg`}>
                        {icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-semibold text-gray-900">
                                {notification.title || 'Notification'}
                              </h3>
                              {notification.is_broadcast && (
                                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                                  Broadcast
                                </span>
                              )}
                              {!notification.is_read && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  New
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>{formatTimeAgo(notification.created_at)}</span>
                              <span className="capitalize text-gray-400">
                                {notification.type?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!notification.is_read && (
                              <button
                                onClick={() => handleMarkAsRead(notification.id)}
                                disabled={markAsReadMutation.isPending}
                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50"
                                title="Mark as read"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(notification.id)}
                              disabled={deleteMutation.isPending}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                              title="Delete notification"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorNotifications;
